import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import DataTable from './shared/DataTable';
import StandaloneSection from './shared/StandaloneSection';
import { getStrategyMeta } from './shared/strategyMeta';
import SinglePositionCycleOrders from './SinglePositionCycleOrders';
import { useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import { getCycleHistory, CycleHistoryParams, CycleSummary } from '../api/singlePositionCycles';
import './shared/MetricCards.css';
import './SinglePositionCycleHistory.css';

const TERMINAL_STATES = new Set(['CYCLE_COMPLETE', 'CYCLE_ABORTED']);

interface Props {
  tradeGroupId: string;
  /** Live price from the detail page — the basis for the in-progress cycle's unrealized P&L (REQ-010). */
  currentPrice?: number | null;
}

function fmtNum(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function pnlClass(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value >= 0 ? 'pnl-positive' : 'pnl-negative';
}

/** Percent move of a price relative to the cycle's entry: (mark − entry) / entry × 100. */
function pctFromEntry(mark: number | null | undefined, entry: number | null | undefined): number | null {
  if (mark === null || mark === undefined || entry === null || entry === undefined || entry === 0) return null;
  return ((mark - entry) / entry) * 100;
}

function fmtPct(value: number | null): string {
  if (value === null) return '';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/** Water-mark cell: the price with its color-coded percent move from entry beneath it. */
function waterMarkCell(
  mark: number | null | undefined,
  entry: number | null | undefined,
  provisional: boolean = false,
): React.ReactNode {
  if (mark === null || mark === undefined) return <span className="price-cell">—</span>;
  const pct = pctFromEntry(mark, entry);
  return (
    <span
      className={`wm-cell${provisional ? ' wm-provisional' : ''}`}
      title={provisional
        ? 'So far — computed live from entry to now; this can still widen while the position is open'
        : undefined}
    >
      <span className="price-cell">{fmtNum(mark)}</span>
      {pct !== null && <span className={`wm-pct ${pnlClass(pct)}`}>{fmtPct(pct)}</span>}
    </span>
  );
}

/**
 * Cycle duration (REQ / operator request): completed → exit − entry; open in-position → now − entry;
 * watching (no entry) → now − created_at. Client-side so an open cycle's duration is fresh at render.
 */
function cycleDurationMs(cycle: CycleSummary, now: number): number | null {
  const entry = cycle.entryTime ? new Date(cycle.entryTime).getTime() : null;
  const exit = cycle.exitTime ? new Date(cycle.exitTime).getTime() : null;
  const created = cycle.createdAt ? new Date(cycle.createdAt).getTime() : null;
  if (entry !== null && exit !== null) return exit - entry;
  if (entry !== null) return now - entry;
  if (created !== null) return now - created;
  return null;
}

function fmtDuration(ms: number | null): string {
  if (ms === null || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** True when the cycle is still open with a held position — i.e. its P&L is unrealized (REQ-010). */
function isInProgress(cycle: CycleSummary): boolean {
  return (
    !!cycle.cycleState &&
    !TERMINAL_STATES.has(cycle.cycleState) &&
    cycle.remainingPositionSize !== null &&
    cycle.remainingPositionSize !== undefined
  );
}

/**
 * P&L as a percent of the capital actually deployed on the entry side.
 *
 * The denominator is server-computed from the exchange fills (Σ filled_size × average_filled_price
 * across entry legs), NOT the trade group's configured size and NOT the cycle's entry_quote_size
 * column — that column holds a base amount on the limit-entry path, which made this percent wrong by
 * roughly the asset's price. A partially filled ladder reports against what it genuinely spent.
 */
function pnlPct(pnl: number | null | undefined, deployedCapital: number | null | undefined): number | null {
  if (pnl === null || pnl === undefined) return null;
  if (deployedCapital === null || deployedCapital === undefined || deployedCapital === 0) return null;
  return (pnl / deployedCapital) * 100;
}

/** Live mark-to-market on the remaining (post-partial-exit) size. Client-side because currentPrice is live. */
function unrealizedPnl(cycle: CycleSummary, currentPrice: number | null | undefined): number | null {
  if (currentPrice === null || currentPrice === undefined) return null;
  if (cycle.entryPrice === null || cycle.remainingPositionSize === null) return null;
  return (currentPrice - cycle.entryPrice) * cycle.remainingPositionSize;
}

/**
 * Cycle-based view for single-position trade groups (spec REQ-009/010/011/013). Replaces the
 * ladder-shaped order table on the detail page. Group summary + server-paginated/sorted cycle table.
 */
const SinglePositionCycleHistory: React.FC<Props> = ({ tradeGroupId, currentPrice }) => {
  const refreshTrigger = useAutoRefreshTrigger('trade-group-detail');

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expandedCycleId, setExpandedCycleId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'cycleNumber', desc: true }]);
  const sortBy = sorting[0]?.id as CycleHistoryParams['sort'] | undefined;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

  const params: CycleHistoryParams = {
    page: pageIndex + 1,
    pageSize,
    sort: sortBy,
    dir: sortDir,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['single-position-cycles', tradeGroupId, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getCycleHistory(tradeGroupId, params),
  });

  const cycles = data?.cycles ?? [];
  const total = data?.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;
  const summary = data?.summary ?? null;
  const meta = getStrategyMeta(data?.tradeGroupType);

  const columns = useMemo<ColumnDef<CycleSummary>[]>(
    () => [
      { id: 'cycleNumber', accessorKey: 'cycleNumber', header: 'Cycle', cell: (i) => i.getValue() ?? '—' },
      {
        accessorKey: 'cycleState',
        header: 'State',
        enableSorting: false,
        cell: (i) => <span className="badge">{(i.getValue() as string | null) ?? '—'}</span>,
      },
      { id: 'entryTime', accessorKey: 'entryTime', header: 'Entry Time', cell: (i) => <span className="date-cell">{fmtTime(i.getValue() as string | null)}</span> },
      { accessorKey: 'entryPrice', header: 'Entry Px', enableSorting: false, cell: (i) => <span className="price-cell">{fmtNum(i.getValue() as number | null)}</span> },
      { accessorKey: 'exitTime', header: 'Exit Time', enableSorting: false, cell: (i) => <span className="date-cell">{fmtTime(i.getValue() as string | null)}</span> },
      { id: 'duration', header: 'Duration', enableSorting: false, cell: (info) => fmtDuration(cycleDurationMs(info.row.original, Date.now())) },
      { accessorKey: 'exitPrice', header: 'Exit Px', enableSorting: false, cell: (i) => <span className="price-cell">{fmtNum(i.getValue() as number | null)}</span> },
      { accessorKey: 'stopPrice', header: 'Stop', enableSorting: false, cell: (i) => <span className="price-cell">{fmtNum(i.getValue() as number | null)}</span> },
      { accessorKey: 'lowWaterMark', header: 'Low WM', enableSorting: false, cell: (info) => waterMarkCell(info.row.original.lowWaterMark, info.row.original.entryPrice, info.row.original.waterMarksProvisional) },
      { accessorKey: 'highWaterMark', header: 'High WM', enableSorting: false, cell: (info) => waterMarkCell(info.row.original.highWaterMark, info.row.original.entryPrice, info.row.original.waterMarksProvisional) },
      {
        id: 'realizedPnl',
        accessorKey: 'realizedPnl',
        header: 'P&L',
        cell: (info) => {
          const cycle = info.row.original;
          // Same value-over-percent shape as the water-mark cells one column over, so the two read
          // consistently. The percent is against deployed capital, NOT the price move in Entry/Exit Px.
          const inProgress = isInProgress(cycle);
          const value = inProgress ? unrealizedPnl(cycle, currentPrice) : cycle.realizedPnl;
          const pct = pnlPct(value, cycle.deployedCapital);
          return (
            <span className="wm-cell">
              <span
                className={inProgress ? `pnl-unrealized ${pnlClass(value)}` : pnlClass(value)}
                title={inProgress ? 'Unrealized — live mark on the remaining position' : undefined}
              >
                {fmtNum(value)}
                {inProgress && <span className="pnl-unrealized-tag">unrealized</span>}
              </span>
              {pct !== null && <span className={`wm-pct ${pnlClass(pct)}`}>{fmtPct(pct)}</span>}
            </span>
          );
        },
      },
      {
        accessorKey: 'isWinner',
        header: 'Outcome',
        enableSorting: false,
        cell: (info) => {
          const cycle = info.row.original;
          if (isInProgress(cycle)) return <span className="badge">OPEN</span>;
          if (cycle.isWinner === null || cycle.isWinner === undefined) return '—';
          return <span className={`badge ${cycle.isWinner ? 'badge-win' : 'badge-loss'}`}>{cycle.isWinner ? 'WIN' : 'LOSS'}</span>;
        },
      },
      { accessorKey: 'exitReason', header: 'Exit Reason', enableSorting: false, cell: (i) => (i.getValue() as string | null) ?? '—' },
      {
        accessorKey: 'remainingPositionSize',
        header: 'Remaining',
        enableSorting: false,
        cell: (i) => {
          const v = i.getValue() as number | null;
          if (v === null || v === undefined) return '—';
          // Positive = base still held (left on the table); negative = over-exited (anomaly); zero = fully
          // closed. Positive is deliberately NOT green — a leftover position is a warning, not a gain.
          const cls = v > 0 ? 'remaining-open' : v < 0 ? 'remaining-negative' : 'remaining-closed';
          const title =
            v > 0
              ? 'Base still held — exit legs did not fully offset the entry fill(s)'
              : v < 0
                ? 'Over-exited — exit legs exceed the entry fill(s) (data anomaly worth investigating)'
                : 'Fully closed — exit legs offset the entry fill(s) exactly';
          return <span className={`price-cell ${cls}`} title={title}>{fmtNum(v, 8)}</span>;
        },
      },
    ],
    [currentPrice]
  );

  return (
    <StandaloneSection title={`${meta.label} — Cycle History`}>
      <div className="metrics-grid spc-summary-grid">
        <SummaryCard label="Cycles" value={summary ? String(summary.cycleCount ?? 0) : '—'} />
        <SummaryCard label="Wins" value={summary ? String(summary.winCount ?? 0) : '—'} className="metric-card-success" />
        <SummaryCard label="Losses" value={summary ? String(summary.lossCount ?? 0) : '—'} className="metric-card-danger" />
        <SummaryCard
          label="Cumulative P&L"
          value={summary ? fmtNum(summary.cumulativePnl) : '—'}
          valueClassName={pnlClass(summary?.cumulativePnl)}
        />
        <SummaryCard label="State" value={summary?.state ?? '—'} />
      </div>

      <DataTable<CycleSummary>
        data={cycles}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s.length ? s : [{ id: 'cycleNumber', desc: true }]);
            setPageIndex(0);
          },
        }}
        isLoading={isLoading}
        loadingMessage="Loading cycles…"
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load cycles'}
        emptyMessage="No cycles yet for this trade group."
        ariaLabel="Single-position cycle history"
        onRowClick={(row) => setExpandedCycleId((prev) => (prev === row.cycleId ? null : row.cycleId))}
        expandedRowContent={(row) =>
          row.cycleId === expandedCycleId ? (
            <SinglePositionCycleOrders tradeGroupId={tradeGroupId} cycleId={row.cycleId} />
          ) : null
        }
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </StandaloneSection>
  );
};

const SummaryCard: React.FC<{ label: string; value: string; className?: string; valueClassName?: string }> = ({
  label,
  value,
  className,
  valueClassName,
}) => (
  <div className={`metric-card ${className ?? ''}`}>
    <div className="metric-label">{label}</div>
    <div className={`metric-value ${valueClassName ?? ''}`}>{value}</div>
  </div>
);

export default SinglePositionCycleHistory;
